<template>
  <main class="main-content flex-grow-1 p-4 overflow-auto mt-6" style="background-color: #f8f9fa">
    <h2 class="text-header-page" style="color: var(--Dark-100, #022326)">
      Balance
    </h2>
    <!-- Sandbox Mode Alert -->
    <div v-if="environment === 'sandbox'" class="alert alert-warning mb-4 text-white" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      You are currently on sandbox mode, where all operations are testing
      purpose only and do not affect live data
    </div>

    <div class="row justify-content-center">
      <div class="col-12 px-0">
        <!-- Balance Card Container -->
        <div class="card card-custom m-4">
          <div class="card-body p-0">
            <BalanceCard :balance="balance" :loading="balanceLoading" @add-balance="showAddBalance = true" />
          </div>
        </div>
        <!-- Activity Table Container -->
        <div class="card card-custom m-4">
          <div class="card-body">
            <div class="activity-section p-0">
              <div class="activity-label">Activity</div>
              <BalanceActivityTable ref="balanceActivityTable" :activities="activities" :loading="activitiesLoading && !isExporting"
                :pagination-data="pagination" :initial-filters="initialFilters" @page-change="handlePageChange"
                @export-csv="handleExportCSV" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <AddBalanceModal :show="showAddBalance" @close="showAddBalance = false" />
  </main>
</template>

<script>
import BalanceCard from "./BalanceCard.vue";
import BalanceActivityTable from "./BalanceActivityTable.vue";
import AddBalanceModal from "./AddBalanceModal.vue";
import { getBalance, historyBalance } from "../../api/balance";

export default {
  name: "BalanceView",
  computed: {
    environment() {
      return this.$store.getters.currentEnvironment;
    },
  },
  components: {
    BalanceCard,
    BalanceActivityTable,
    AddBalanceModal,
  },
  data() {
    const balanceValue = 0;
    const activitiesData = [];
    return {
      balance: balanceValue,
      activities: activitiesData,
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null
      },
      showAddBalance: false,
      balanceLoading: false,
      activitiesLoading: false,
      isExporting: false, // Track if export is in progress
      initialFilters: {},
    };
  },
  methods: {
    async fetchBalance() {
      this.balanceLoading = true;
      try {
        const response = await getBalance();
        if (response && response.status === "success" && response.data && response.data.merchant) {
          this.balance = response.data.merchant.balance || 0;
        } else {
          console.warn('Balance API response structure unexpected:', response);
          this.balance = 0;
        }
      } catch (error) {
        console.error('Error fetching balance:', error);
        this.balance = 0;
      } finally {
        this.balanceLoading = false;
      }
    },
    async fetchHistoryBalance(page = 1, perPage = 20, filters = {}) {
      this.activitiesLoading = true;
      try {
        // Prepare pagination parameters
        const params = {
          page: page,
          per_page: perPage
        };

        // Add filter parameters if they exist
        if (filters.search && filters.search.trim() !== "") {
          params.ref_id = filters.search.trim();
        }

        if (filters.status && filters.status.trim() !== "") {
          params.source = filters.status.trim();
        }

        if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2 && filters.dateRange[0] && filters.dateRange[1]) {
          const startDate = this.formatDateToAPI(new Date(filters.dateRange[0]));
          const endDate = this.formatDateToAPI(new Date(filters.dateRange[1]));
          params.date_range = `${startDate} - ${endDate}`;
        }

        const response = await historyBalance(params);
        if (response && response.status === "success" && response.data) {
          // Update activities data
          if (response.data.data) {
            this.activities = response.data.data;
          } else {
            console.warn('History balance API response missing data array:', response);
            this.activities = [];
          }

          // Update pagination data if available
          if (response.data.pagination) {
            this.pagination = response.data.pagination;
          }
        } else {
          console.warn('History balance API response structure unexpected:', response);
          this.activities = [];
        }
      } catch (error) {
        console.error('Error fetching balance history:', error);
        this.activities = [];
      } finally {
        this.activitiesLoading = false;
      }
    },

    // Handle page change from child component
    handlePageChange(page, perPage, filters = {}) {
      this.fetchHistoryBalance(page, perPage, filters);
    },

    // Format date to API format (YYYY-MM-DD)
    formatDateToAPI(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${day}/${month}/${year}`;
    },

    // Handle export CSV with filters
    async handleExportCSV(filters) {
      // Validasi: export hanya boleh untuk range 1 hari (tanggal mulai dan akhir sama)
      if (
        filters.dateRange &&
        Array.isArray(filters.dateRange) &&
        filters.dateRange.length === 2 &&
        filters.dateRange[0] &&
        filters.dateRange[1]
      ) {
        const startDate = new Date(filters.dateRange[0]);
        const endDate = new Date(filters.dateRange[1]);

        // Bandingkan hanya berdasarkan tanggal (tanpa jam)
        const isSameDay = startDate.toDateString() === endDate.toDateString();

        if (!isSameDay) {
          this.$swal({
            title: 'Invalid Date Range',
            text: 'Export hanya bisa untuk rentang 1 hari. Silakan pilih tanggal yang sama untuk Start dan End.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
          return;
        }
      }

      try {
        this.activitiesLoading = true;
        this.isExporting = true; // Set flag to hide default loading

        // Show loading alert for export process
        this.$swal({
          title: 'Preparing Export...',
          html: `
            <div style="text-align: center; padding: 20px 0;">
              <p style="margin: 10px 0; color: #495057; font-size: 14px;">
                Sedang memproses data balance activity untuk di-export...
              </p>
              <p style="margin: 10px 0; color: #6c757d; font-size: 12px;">
                Untuk data yang banyak, proses ini mungkin membutuhkan waktu beberapa saat.<br>
                Mohon tunggu dan jangan tutup halaman ini.
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Build params for backend export API
        const params = {};

        // Add date range filter if set
        if (
          filters.dateRange &&
          Array.isArray(filters.dateRange) &&
          filters.dateRange.length === 2 &&
          filters.dateRange[0] &&
          filters.dateRange[1]
        ) {
          const startDate = this.formatDateToAPI(new Date(filters.dateRange[0]));
          const endDate = this.formatDateToAPI(new Date(filters.dateRange[1]));
          params.date_range = `${startDate} - ${endDate}`;
        }

        // Add source filter if set
        if (filters.status && filters.status.trim() !== "") {
          params.source = filters.status.trim();
        }

        // Add ref_id filter if set
        if (filters.search && filters.search.trim() !== "") {
          params.ref_id = filters.search.trim();
        }

        // Show loading alert for download
        this.$swal({
          title: 'Downloading...',
          html: `
            <div style="text-align: center; padding: 20px 0;">
              <p style="margin: 10px 0; color: #495057; font-size: 14px;">
                Sedang download, mohon ditunggu...
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Fetch data
        const backendApi = process.env.BACKEND_API || "https://api.swiftrans.id";
        const token = localStorage.getItem("auth_token");
        let exportUrl = `${backendApi}/api/merchant/history-balances/export`;
        
        // Build query string
        const queryParams = new URLSearchParams();
        if (params.date_range) queryParams.append('date_range', params.date_range);
        if (params.source) queryParams.append('source', params.source);
        if (params.ref_id) queryParams.append('ref_id', params.ref_id);
        
        if (queryParams.toString()) {
          exportUrl += `?${queryParams.toString()}`;
        }

        const response = await fetch(exportUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download file');
        }

        // Get blob directly
        const blob = await response.blob();
        
        // Close progress alert
        this.$swal.close();

        if (!blob || blob.size === 0) {
          this.$swal({
            title: 'No Data',
            text: 'No data to export',
            icon: 'info',
            confirmButtonText: 'OK'
          });
          return;
        }

        // Generate informative filename
        const now = new Date();
        const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format
        const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, ""); // HHMMSS format
        
        // Build filename with filter info
        let filterInfo = "";
        if (filters.status) filterInfo += `_source-${filters.status.replace(/\s+/g, "-").toLowerCase()}`;
        if (filters.search) filterInfo += `_refid-${filters.search.substring(0, 10)}`;
        if (filters.dateRange && filters.dateRange.length === 2) {
          const startDate = new Date(filters.dateRange[0]);
          const endDate = new Date(filters.dateRange[1]);
          filterInfo += `_from-${startDate.toISOString().split("T")[0]}_to-${endDate.toISOString().split("T")[0]}`;
        }
        
        const filename = `balance-history${filterInfo}_${dateStr}_${timeStr}.csv`;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$swal({
          title: 'Success!',
          text: 'Balance activity exported successfully',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } catch (error) {
        console.error('Error exporting CSV:', error);
        // Close loading alert if still open
        this.$swal.close();
        this.$swal({
          title: 'Export Failed',
          text: error.message || 'Error exporting data. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.activitiesLoading = false;
        this.isExporting = false; // Reset flag to show default loading again
      }
    },
    stripTime(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },

    // Extract URL parameters for initial filtering
    extractUrlParameters() {
      const query = this.$route.query;
      const filters = {};

      if (query.ref_id) {
        filters.search = query.ref_id;
      }

      if (query.source) {
        filters.status = query.source;
      }

      if (query.start_date && query.end_date) {
        filters.dateRange = [
          new Date(query.start_date),
          new Date(query.end_date)
        ];
      }

      return filters;
    },
  },
  mounted() {
    // Extract URL parameters for initial filtering
    this.initialFilters = this.extractUrlParameters();

    this.fetchBalance();
    // The BalanceActivityTable child component will handle the initial history fetch
    // with its default date filter (today) or with URL parameters if provided
  },
};
</script>

<style scoped>
.activity-label {
  color: #121313;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 15px;
  /* atas kanan bawah kiri */
}

.title {
  font-weight: bold;
  font-size: 1.75rem;
  letter-spacing: -0.5px;
  color: #111827;
  margin: 0 0 0 15px;
}
</style>
